import { useState, useEffect, useCallback, useRef } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { organizationalUnitsApi } from '../../services/apiService';
import { treeToNodesEdges, collectAllIds } from './orgChartUtils';
import type { TreeNode, TreeResponse } from './orgChartUtils';

interface UseOrgChartDataOptions {
  organizationId?: number;
  organizationName?: string;
  onNodeClick: (node: TreeNode) => void;
}

export function useOrgChartData({ organizationId, organizationName, onNodeClick }: UseOrgChartDataOptions) {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState('#2563EB');
  const [secondaryColor, setSecondaryColor] = useState('#A6813F');
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<number>>(new Set());
  const treeDataRef = useRef<TreeNode[]>([]);
  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;

  const toggleExpand = useCallback((nodeId: number) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const buildNodesEdges = useCallback(
    (tree: TreeNode[], expanded: Set<number>, pColor: string, sColor: string) => {
      // If organizationName is provided, wrap in a synthetic root
      let processedTree = tree;
      if (tree.length > 0 && organizationName) {
        // Set children's parent to -1 so edges connect them to the synthetic root
        const wrappedChildren = tree.map((child) => ({ ...child, parent: -1 }));
        const syntheticRoot: TreeNode = {
          id: -1,
          name: organizationName,
          name_ar: organizationName,
          unit_type: 'department',
          parent: null,
          order: 0,
          employees_count: 0,
          children: wrappedChildren,
        };
        processedTree = [syntheticRoot];
      }

      const { nodes: n, edges: e } = treeToNodesEdges(processedTree, {
        primaryColor: pColor,
        secondaryColor: sColor,
        expandedNodeIds: expanded,
        onToggleExpand: toggleExpand,
        onNodeClick: (node) => onNodeClickRef.current(node),
      });
      setNodes(n);
      setEdges(e);
    },
    [organizationName, toggleExpand],
  );

  const fetchTree = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response: TreeResponse = await organizationalUnitsApi.tree(
        organizationId ? { organization: organizationId } : undefined,
      );
      const tree = response.tree;
      const pColor = response.primary_color || '#2563EB';
      const sColor = response.secondary_color || '#A6813F';

      setPrimaryColor(pColor);
      setSecondaryColor(sColor);
      treeDataRef.current = tree;

      // Expand all nodes by default (same behavior as old component)
      const allTree = organizationName
        ? [{ id: -1, name: organizationName, name_ar: organizationName, unit_type: 'department' as const, parent: null, order: 0, employees_count: 0, children: tree }]
        : tree;
      const allIds = new Set(collectAllIds(allTree));
      setExpandedNodeIds(allIds);

      buildNodesEdges(tree, allIds, pColor, sColor);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load org chart';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, organizationName, buildNodesEdges]);

  // Re-build nodes/edges when expandedNodeIds changes
  useEffect(() => {
    if (treeDataRef.current.length > 0) {
      buildNodesEdges(treeDataRef.current, expandedNodeIds, primaryColor, secondaryColor);
    }
  }, [expandedNodeIds, buildNodesEdges, primaryColor, secondaryColor]);

  // Initial fetch
  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  return {
    nodes,
    edges,
    loading,
    error,
    primaryColor,
    secondaryColor,
    refreshTree: fetchTree,
    toggleExpand,
  };
}
